// ============================================================
//  src/pages/bots/tabs/BroadcastsTab.js
// ============================================================
import React, { useEffect, useState, useCallback } from 'react';
import {
  listBroadcasts, createBroadcast, updateBroadcast,
  sendBroadcast, resendBroadcast, stopBroadcast, deleteBroadcast, getBroadcast,
  pauseBroadcast, resumeBroadcast, testSendBroadcast, previewBroadcastAudience,
  uploadBroadcastImage,
} from '../../../api/client';
import toast from 'react-hot-toast';
import './tabs.css';
import './BroadcastsTab.css';

// ── Helpers ────────────────────────────────────────────────────
function fmt(dt) {
  if (!dt) return '—';
  return new Date(dt.replace(' ', 'T')).toLocaleString(undefined, {
    dateStyle: 'medium', timeStyle: 'short',
  });
}
function pct(n, total) {
  if (!total) return 0;
  return Math.round((n / total) * 100);
}

const STATUS_META = {
  draft:     { label: 'Draft',      color: '#6b7280', bg: '#f3f4f6' },
  scheduled: { label: 'Scheduled',  color: '#d97706', bg: '#fffbeb' },
  sending:   { label: 'Sending…',   color: '#2563eb', bg: '#eff6ff' },
  active:    { label: 'Running…',   color: '#7c3aed', bg: '#f5f3ff' },
  sent:      { label: 'Sent',       color: '#16a34a', bg: '#f0fdf4' },
  failed:    { label: 'Failed',     color: '#dc2626', bg: '#fef2f2' },
  recurring: { label: 'Recurring',  color: '#0891b2', bg: '#ecfeff' },
  stopped:   { label: 'Stopped',    color: '#6b7280', bg: '#f3f4f6' },
  paused:    { label: 'Paused',     color: '#b45309', bg: '#fffbeb' },
};

// ── Recurrence label helper ────────────────────────────────────
function recurrenceLabel(raw) {
  if (!raw) return null;
  try {
    const r = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (r.type === 'daily')    return '🔁 Every day';
    if (r.type === 'interval') return `🔁 Every ${r.days} day${r.days === 1 ? '' : 's'}`;
    if (r.type === 'weekdays') {
      const names = { mon:'Mon',tue:'Tue',wed:'Wed',thu:'Thu',fri:'Fri',sat:'Sat',sun:'Sun' };
      return '🔁 ' + (r.days || []).map(d => names[d] || d).join(', ');
    }
  } catch {}
  return null;
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return (
    <span className="bc-badge" style={{ color: m.color, background: m.bg }}>
      {m.label}
    </span>
  );
}

// ── Image upload field — picks a PNG/JPG/WEBP file, uploads it,
//    and stores the returned public URL. Used for header images
//    and the standalone Image message type. ─────────────────────
function ImageUploadField({ bId, value, onChange, label, hint }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file later
    if (!file) return;

    if (!['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(file.type)) {
      toast.error('Please choose a PNG, JPG, WEBP, or GIF image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    setUploading(true);
    try {
      const form = new FormData();
      form.append('bId', bId);
      form.append('action', 'upload_image');
      form.append('image', file);
      const res = await uploadBroadcastImage(form);
      onChange(res.data.url);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div style={{ marginTop: 10 }}>
      {label && <label className="field-label">{label}</label>}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        style={{ display: 'none' }}
        onChange={handleFile}
      />

      {!value ? (
        <button
          type="button"
          className="btn btn-outline btn-sm bc-upload-btn"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? (<><span className="spinner spinner-dark" /> Uploading…</>) : '📁 Upload image'}
        </button>
      ) : (
        <div className="bc-image-preview">
          <img src={value} alt="Header" />
          <div className="bc-image-preview-actions">
            <button type="button" className="btn btn-outline btn-sm"
              disabled={uploading}
              onClick={() => inputRef.current?.click()}>
              {uploading ? 'Uploading…' : 'Replace'}
            </button>
            <button type="button" className="btn btn-outline btn-sm bc-delete-btn"
              disabled={uploading}
              onClick={() => onChange('')}>
              Remove
            </button>
          </div>
        </div>
      )}
      {hint && <p className="bc-hint">{hint}</p>}
    </div>
  );
}

function ProgressBar({ value, color }) {
  return (
    <div className="bc-progress-track">
      <div className="bc-progress-fill"
        style={{ width: `${Math.min(value, 100)}%`, background: color }} />
    </div>
  );
}

// ── Campaign list row ──────────────────────────────────────────
function CampaignRow({ c, onView, onSend, onResend, onStop, onPause, onResume, onTestSend, onEdit, onDelete }) {
  const sentPct      = pct(c.bSentCount,   c.bTotalCount);
  const canSend      = c.bStatus === 'draft';
  const canResend    = c.bStatus === 'sent' || c.bStatus === 'failed' || c.bStatus === 'recurring' || c.bStatus === 'stopped';
  const canStop      = c.bStatus === 'sending' || c.bStatus === 'scheduled' || c.bStatus === 'active' || c.bStatus === 'recurring';
  const canPause     = c.bStatus === 'sending' || c.bStatus === 'active';
  const canResume    = c.bStatus === 'paused';
  const canTestSend  = true; // any campaign with a saved message can be test-sent
  const canDelete    = c.bStatus !== 'sending';
  const hasSendStats = c.bTotalCount > 0;
  const isInactivity = c.bSendType === 'inactivity';
  const recLabel     = recurrenceLabel(c.bRecurrence);
  const canEdit = c.bStatus === 'draft' || c.bStatus === 'scheduled'
    || c.bStatus === 'sent' || c.bStatus === 'failed' || c.bStatus === 'recurring' || c.bStatus === 'stopped'
    || (c.bStatus === 'active' && !c.bTotalCount);

  return (
    <div className="bc-row" onClick={() => onView(c)}>
      <div className="bc-row-main">
        <div className="bc-row-name">{c.bName}</div>
        <div className="bc-row-meta">
          <StatusBadge status={c.bStatus} />
          {recLabel && (
            <span className="bc-row-time" style={{ color: '#0891b2' }}>{recLabel}</span>
          )}
          {c.bNextRunAt && c.bStatus === 'recurring' && (
            <span className="bc-row-time">Next: {fmt(c.bNextRunAt)}</span>
          )}
          {c.bScheduledAt && c.bStatus === 'scheduled' && (
            <span className="bc-row-time">📅 {fmt(c.bScheduledAt)}</span>
          )}
          {isInactivity && (
            <span className="bc-row-time">⏱ {c.bInactivityHours}h after inactivity</span>
          )}
          {c.bCreatedAt && (
            <span className="bc-row-time">Created {fmt(c.bCreatedAt)}</span>
          )}
        </div>
      </div>

      {hasSendStats && (
        <div className="bc-row-stats">
          <div className="bc-stat">
            <span className="bc-stat-label">Sent</span>
            <span className="bc-stat-num">{c.bSentCount.toLocaleString()}</span>
            <ProgressBar value={sentPct} color="#16a34a" />
          </div>
          <div className="bc-stat">
            <span className="bc-stat-label">Failed</span>
            <span className="bc-stat-num">{c.bFailedCount.toLocaleString()}</span>
          </div>
          <div className="bc-stat">
            <span className="bc-stat-label">Total</span>
            <span className="bc-stat-num">{c.bTotalCount.toLocaleString()}</span>
          </div>
        </div>
      )}

      <div className="bc-row-actions" onClick={e => e.stopPropagation()}>
        {canSend && (
          <button className="btn btn-primary btn-sm" onClick={() => onSend(c)}>
            ▶ Send
          </button>
        )}
        {canResend && (
          <button className="btn btn-primary btn-sm" onClick={() => onResend(c)}>
            🔁 Resend
          </button>
        )}
        {canPause && (
          <button className="btn btn-outline btn-sm" style={{ color: '#b45309', borderColor: '#b45309' }}
            onClick={() => onPause(c)}>
            ⏸ Pause
          </button>
        )}
        {canResume && (
          <button className="btn btn-primary btn-sm" onClick={() => onResume(c)}>
            ▶ Resume
          </button>
        )}
        {canStop && (
          <button className="btn btn-outline btn-sm" style={{ color: '#dc2626', borderColor: '#dc2626' }}
            onClick={() => onStop(c)}>
            ⏹ Stop
          </button>
        )}
        {canTestSend && (
          <button className="btn btn-outline btn-sm" onClick={() => onTestSend(c)}>
            🧪 Test
          </button>
        )}
        {canEdit && (
          <button className="btn btn-outline btn-sm" onClick={() => onEdit(c)}>
            ✏️ Edit
          </button>
        )}
        {canDelete && (
          <button className="btn btn-outline btn-sm bc-delete-btn"
            onClick={() => onDelete(c)}>
            🗑
          </button>
        )}
      </div>
    </div>
  );
}

// ── New / Edit Campaign Wizard ──────────────────────────────────
const STEPS = ['Audience', 'Message', 'Schedule'];

function NewBroadcastModal({ bId, commands, campaign, onSaved, onClose }) {
  const isEdit = !!campaign;

  // Parse the stored JSON blobs once, up front, so all the step state
  // below can just initialise from plain values whether creating or editing.
  const initAudience = campaign ? (JSON.parse(campaign.bAudience || '{}') || {}) : {};
  const initMessage  = campaign ? (JSON.parse(campaign.bMessage  || '{}') || {}) : {};

  const [step, setStep]   = useState(0);
  const [saving, setSaving] = useState(false);

  // Step 0 — Audience
  const [audienceType, setAudienceType] = useState(initAudience.type ?? 'all');
  const [activeDays, setActiveDays]     = useState(initAudience.days ?? 7);
  const [storageKey, setStorageKey]     = useState(initAudience.storageKey ?? '');
  const [storageVal, setStorageVal]     = useState(initAudience.storageValue ?? '');
  const [lastCmd, setLastCmd]           = useState(initAudience.command ?? '');

  // Step 1 — Message
  const [name, setName]           = useState(campaign?.bName ?? '');
  const [msgType, setMsgType]     = useState(initMessage.type ?? 'text');
  const [msgText, setMsgText]     = useState(initMessage.text ?? '');
  const [imageUrl, setImageUrl]   = useState(initMessage.imageUrl ?? '');
  const [caption, setCaption]     = useState(initMessage.caption ?? '');
  const [templateName, setTName]  = useState(initMessage.templateName ?? '');
  const [langCode, setLangCode]   = useState(initMessage.langCode ?? 'en_US');
  const [headerImageUrl, setHeaderImageUrl] = useState(initMessage.headerImageUrl ?? '');
  const [ctaUrl, setCtaUrl]           = useState(initMessage.url ?? '');
  const [ctaButtonText, setCtaButtonText] = useState(initMessage.buttonText ?? '');
  const [buttons, setButtons]     = useState(
    (initMessage.buttons && initMessage.buttons.length
      ? initMessage.buttons.map(b => ({ id: b.id || '', title: b.title || '', command: b.command || '' }))
      : [{ id: '', title: '', command: '' }])
  );

  // Step 2 — Schedule
  const [when, setWhen]                       = useState(campaign?.bSendType ?? 'now'); // 'now' | 'scheduled' | 'inactivity'
  const [schedAt, setSchedAt]                 = useState(
    campaign?.bScheduledAt ? campaign.bScheduledAt.replace(' ', 'T').slice(0, 16) : ''
  );
  const [inactivityHours, setInactivityHours] = useState(campaign?.bInactivityHours ?? 24);

  // Recurrence — parsed from bRecurrence JSON if editing
  const initRec = (() => {
    try { return campaign?.bRecurrence ? JSON.parse(campaign.bRecurrence) : null; } catch { return null; }
  })();
  const [recEnabled,   setRecEnabled]   = useState(!!initRec);
  const [recType,      setRecType]      = useState(initRec?.type ?? 'daily');
  const [recInterval,  setRecInterval]  = useState(initRec?.days ?? 2);
  const [recWeekdays,  setRecWeekdays]  = useState(initRec?.days ?? []);

  // "After inactivity" only makes sense against the "stopped at command"
  // audience — fall back to "now" if the user switches audience away.
  useEffect(() => {
    if (when === 'inactivity' && audienceType !== 'last_command') {
      setWhen('now');
    }
  }, [audienceType, when]);

  function buildAudience() {
    if (audienceType === 'all')          return { type: 'all' };
    if (audienceType === 'active_days')  return { type: 'active_days', days: activeDays };
    if (audienceType === 'storage_key')  return { type: 'storage_key', storageKey, storageValue: storageVal };
    if (audienceType === 'last_command') return { type: 'last_command', command: lastCmd };
    return { type: 'all' };
  }

  function buildMessage() {
    if (msgType === 'text')    return { type: 'text', text: msgText };
    if (msgType === 'buttons') return {
      type: 'buttons', text: msgText,
      headerImageUrl: headerImageUrl.trim() || undefined,
      buttons: buttons
        .filter(b => b.title.trim())
        .map(b => ({
          title:   b.title.trim(),
          id:      b.command ? b.command : (b.id || b.title.trim()),
          command: b.command || null,
        }))
    };
    if (msgType === 'cta') return {
      type: 'cta',
      text: msgText,
      url: ctaUrl.trim(),
      buttonText: ctaButtonText.trim() || 'Open Link',
      headerImageUrl: headerImageUrl.trim() || undefined,
    };
    if (msgType === 'image')   return { type: 'image', imageUrl, caption };
    if (msgType === 'template') return { type: 'template', templateName, langCode };
    return { type: 'text', text: msgText };
  }

  function buildRecurrence() {
    if (!recEnabled || when === 'inactivity') return null;
    if (recType === 'daily')    return { type: 'daily' };
    if (recType === 'interval') return { type: 'interval', days: Math.max(1, recInterval) };
    if (recType === 'weekdays') return recWeekdays.length ? { type: 'weekdays', days: recWeekdays } : null;
    return null;
  }

  function canNext() {
    if (step === 0) {
      if (audienceType === 'storage_key' && (!storageKey || !storageVal)) return false;
      if (audienceType === 'last_command' && !lastCmd) return false;
      return true;
    }
    if (step === 1) {
      if (!name.trim()) return false;
      if (msgType === 'text' || msgType === 'buttons') return msgText.trim().length > 0;
      if (msgType === 'cta') return msgText.trim().length > 0 && ctaUrl.trim().length > 0;
      if (msgType === 'image') return imageUrl.trim().length > 0;
      if (msgType === 'template') return templateName.trim().length > 0;
    }
    if (step === 2) {
      if (when === 'scheduled' && !schedAt) return false;
      if (when === 'inactivity' && (!inactivityHours || inactivityHours < 1)) return false;
      if (recEnabled && recType === 'weekdays' && recWeekdays.length === 0) return false;
      return true;
    }
    return true;
  }

  async function handleSave() {
    setSaving(true);
    try {
      const scheduledAt = when === 'scheduled' ? schedAt.replace('T', ' ') : null;
      const payload = {
        name:            name.trim(),
        audience:        buildAudience(),
        message:         buildMessage(),
        scheduledAt,
        sendType:        when,
        inactivityHours: when === 'inactivity' ? inactivityHours : null,
        recurrence:      buildRecurrence(),
      };
      if (isEdit) {
        await updateBroadcast(campaign.bId, { bId, ...payload });
        toast.success('Campaign updated!');
      } else {
        await createBroadcast({ bId, ...payload });
        toast.success('Campaign created!');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} campaign`);
    } finally {
      setSaving(false);
    }
  }

  const addBtn    = () => setButtons(b => [...b, { id: '', title: '', command: '' }]);
  const removeBtn = (i) => setButtons(b => b.filter((_, j) => j !== i));
  const updateBtn = (i, field, val) => setButtons(b =>
    b.map((btn, j) => j === i ? { ...btn, [field]: val } : btn));

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal bc-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{isEdit ? '✏️ Edit Campaign' : '📢 New Campaign'}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Step indicator */}
        <div className="bc-steps">
          {STEPS.map((s, i) => (
            <div key={s} className={`bc-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
              <div className="bc-step-dot">{i < step ? '✓' : i + 1}</div>
              <div className="bc-step-label">{s}</div>
            </div>
          ))}
        </div>

        <div className="modal-body bc-modal-body">

          {/* ── STEP 0: Audience ── */}
          {step === 0 && (
            <div className="bc-step-content">
              <label className="field-label">Who should receive this campaign?</label>
              <div className="bc-radio-group">
                {[
                  ['all',          '👥 All users'],
                  ['active_days',  '⚡ Active in last N days'],
                  ['storage_key',  '🗄 Storage key = value'],
                  ['last_command', '🔀 Stopped at a specific command'],
                ].map(([val, label]) => (
                  <label key={val} className={`bc-radio-card ${audienceType === val ? 'selected' : ''}`}>
                    <input type="radio" name="audience" value={val}
                      checked={audienceType === val}
                      onChange={() => setAudienceType(val)} />
                    {label}
                  </label>
                ))}
              </div>

              {audienceType === 'active_days' && (
                <div className="bc-sub-field">
                  <label className="field-label">Active in last</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="number" className="form-input" style={{ width: 80 }}
                      value={activeDays} min={1} max={365}
                      onChange={e => setActiveDays(+e.target.value)} />
                    <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>days</span>
                  </div>
                </div>
              )}

              {audienceType === 'storage_key' && (
                <div className="bc-sub-field">
                  <label className="field-label">Storage key</label>
                  <input className="form-input" placeholder="e.g. plan"
                    value={storageKey} onChange={e => setStorageKey(e.target.value)} />
                  <label className="field-label" style={{ marginTop: 10 }}>equals</label>
                  <input className="form-input" placeholder="e.g. premium"
                    value={storageVal} onChange={e => setStorageVal(e.target.value)} />
                </div>
              )}

              {audienceType === 'last_command' && (
                <div className="bc-sub-field">
                  <label className="field-label">Users who stopped at command</label>
                  {commands.length > 0 ? (
                    <select className="form-input" value={lastCmd}
                      onChange={e => setLastCmd(e.target.value)}>
                      <option value="">— pick a command —</option>
                      {commands.map(c => (
                        <option key={c.cmd} value={c.cmd}>
                          {c.cmd}{c.description ? ` — ${c.description}` : ''}{c.cnt > 0 ? ` (${c.cnt} users stopped here)` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input className="form-input"
                      placeholder="/register  (no data yet)"
                      value={lastCmd} onChange={e => setLastCmd(e.target.value)} />
                  )}
                  <p className="bc-hint">
                    Targets users whose last interaction was this command — great for
                    re-engaging people who started a flow but never finished.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 1: Message ── */}
          {step === 1 && (
            <div className="bc-step-content">
              <label className="field-label">Campaign name</label>
              <input className="form-input" placeholder="e.g. June Promo"
                value={name} onChange={e => setName(e.target.value)} />

              <label className="field-label" style={{ marginTop: 16 }}>Message type</label>
              <div className="bc-type-tabs">
                {['text', 'buttons', 'cta', 'image', 'template'].map(t => (
                  <button key={t}
                    className={`bc-type-tab ${msgType === t ? 'active' : ''}`}
                    onClick={() => setMsgType(t)}>
                    {{ text: '💬 Text', buttons: '🔘 Buttons', cta: '🔗 CTA Button', image: '🖼 Image', template: '📋 Template' }[t]}
                  </button>
                ))}
              </div>

              {(msgType === 'text' || msgType === 'buttons' || msgType === 'cta') && (
                <>
                  <label className="field-label" style={{ marginTop: 14 }}>Message text</label>
                  <textarea className="form-input bc-textarea" rows={4}
                    placeholder="Hello {{name}}, we have something for you…"
                    value={msgText} onChange={e => setMsgText(e.target.value)} />
                </>
              )}

              {(msgType === 'buttons' || msgType === 'cta') && (
                <ImageUploadField
                  bId={bId}
                  value={headerImageUrl}
                  onChange={setHeaderImageUrl}
                  label="Header image (optional)"
                  hint="Shown above the message as a header image. WhatsApp only allows one header — if you set this, it replaces any header text. PNG, JPG, WEBP or GIF, max 5MB."
                />
              )}

              {msgType === 'buttons' && (
                <div className="bc-buttons-editor">
                  <label className="field-label">Buttons (max 3)</label>
                  {buttons.map((btn, i) => (
                    <div key={i} className="bc-btn-block">
                      <div className="bc-btn-row">
                        <input className="form-input" placeholder={`Button ${i + 1} label`}
                          style={{ flex: 1 }}
                          value={btn.title}
                          onChange={e => updateBtn(i, 'title', e.target.value)} />
                        {buttons.length > 1 && (
                          <button className="btn btn-outline btn-sm"
                            onClick={() => removeBtn(i)}>✕</button>
                        )}
                      </div>
                      <div className="bc-btn-sub">
                        <select className="form-input bc-btn-cmd-select"
                          value={btn.command}
                          onChange={e => updateBtn(i, 'command', e.target.value)}>
                          <option value="">No command trigger (optional)</option>
                          {commands.map(c => (
                            <option key={c.cmd} value={c.cmd}>{c.cmd}{c.description ? ` — ${c.description}` : ''}</option>
                          ))}
                        </select>
                        {btn.command && (
                          <span className="bc-btn-cmd-hint">
                            ⚡ Tapping this button will trigger <b>{btn.command}</b>
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {buttons.length < 3 && (
                    <button className="btn btn-outline btn-sm" onClick={addBtn}
                      style={{ marginTop: 6 }}>+ Add button</button>
                  )}
                </div>
              )}

              {msgType === 'cta' && (
                <div className="bc-sub-field">
                  <label className="field-label">Button label</label>
                  <input className="form-input" placeholder="e.g. View Pricing"
                    maxLength={20}
                    value={ctaButtonText} onChange={e => setCtaButtonText(e.target.value)} />
                  <p className="bc-hint">Max 20 characters — WhatsApp cuts off anything longer.</p>

                  <label className="field-label" style={{ marginTop: 10 }}>Button URL</label>
                  <input className="form-input" placeholder="https://yourdomain.com/pricing"
                    value={ctaUrl} onChange={e => setCtaUrl(e.target.value)} />
                  <p className="bc-hint">
                    This is a single external-link button (WhatsApp's "Call To Action" type) — it can't be
                    combined with quick-reply buttons in the same message.
                  </p>
                </div>
              )}

              {msgType === 'image' && (
                <>
                  <ImageUploadField
                    bId={bId}
                    value={imageUrl}
                    onChange={setImageUrl}
                    label="Image"
                    hint="PNG, JPG, WEBP or GIF, max 5MB."
                  />
                  <label className="field-label" style={{ marginTop: 10 }}>Caption (optional)</label>
                  <textarea className="form-input bc-textarea" rows={2}
                    value={caption} onChange={e => setCaption(e.target.value)} />
                </>
              )}

              {msgType === 'template' && (
                <>
                  <label className="field-label" style={{ marginTop: 14 }}>Template name</label>
                  <input className="form-input" placeholder="hello_world"
                    value={templateName} onChange={e => setTName(e.target.value)} />
                  <label className="field-label" style={{ marginTop: 10 }}>Language code</label>
                  <input className="form-input" placeholder="en_US"
                    value={langCode} onChange={e => setLangCode(e.target.value)} />
                  <p className="bc-hint">
                    Templates are required for users outside the 24-hour messaging window.
                    The template must be approved by Meta before use.
                  </p>
                </>
              )}
            </div>
          )}

          {/* ── STEP 2: Schedule ── */}
          {step === 2 && (
            <div className="bc-step-content">
              <label className="field-label">When to send</label>
              <div className="bc-radio-group">
                <label className={`bc-radio-card ${when === 'now' ? 'selected' : ''}`}>
                  <input type="radio" name="when" value="now"
                    checked={when === 'now'} onChange={() => setWhen('now')} />
                  ⚡ Send now
                </label>
                <label className={`bc-radio-card ${when === 'scheduled' ? 'selected' : ''}`}>
                  <input type="radio" name="when" value="scheduled"
                    checked={when === 'scheduled'} onChange={() => setWhen('scheduled')} />
                  📅 Schedule for later
                </label>
                {audienceType === 'last_command' && (
                  <label className={`bc-radio-card ${when === 'inactivity' ? 'selected' : ''}`}>
                    <input type="radio" name="when" value="inactivity"
                      checked={when === 'inactivity'} onChange={() => setWhen('inactivity')} />
                    ⏱ After a period of inactivity
                  </label>
                )}
              </div>

              {when === 'scheduled' && (
                <div className="bc-sub-field">
                  <label className="field-label">Date & time</label>
                  <input type="datetime-local" className="form-input"
                    value={schedAt}
                    min={new Date().toISOString().slice(0, 16)}
                    onChange={e => setSchedAt(e.target.value)} />
                </div>
              )}

              {when === 'inactivity' && (
                <div className="bc-sub-field">
                  <label className="field-label">Send after</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="number" className="form-input" style={{ width: 80 }}
                      value={inactivityHours} min={1} max={720}
                      onChange={e => setInactivityHours(+e.target.value)} />
                    <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>hours of inactivity</span>
                  </div>
                  <p className="bc-hint">
                    This campaign runs continuously in the background. Any user who
                    stops at <b>{lastCmd || '?'}</b> and sends nothing else for{' '}
                    <b>{inactivityHours || 0} hour{inactivityHours === 1 ? '' : 's'}</b> will
                    automatically receive this message — even users who reach{' '}
                    {lastCmd || 'this command'} after you create the campaign.
                  </p>
                </div>
              )}

              {/* ── Recurrence (only for non-inactivity sends) ── */}
              {when !== 'inactivity' && (
                <div className="bc-sub-field" style={{ marginTop: 20 }}>
                  <label className="field-label" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="checkbox" checked={recEnabled}
                      onChange={e => setRecEnabled(e.target.checked)} />
                    🔁 Repeat this campaign
                  </label>

                  {recEnabled && (
                    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div className="bc-radio-group" style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                        {[
                          ['daily',    '📅 Every day'],
                          ['interval', '📆 Every N days'],
                          ['weekdays', '🗓 Specific days'],
                        ].map(([val, label]) => (
                          <label key={val} className={`bc-radio-card ${recType === val ? 'selected' : ''}`}
                            style={{ flex: '1 1 120px' }}>
                            <input type="radio" name="recType" value={val}
                              checked={recType === val} onChange={() => setRecType(val)} />
                            {label}
                          </label>
                        ))}
                      </div>

                      {recType === 'interval' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>Every</span>
                          <input type="number" className="form-input" style={{ width: 72 }}
                            value={recInterval} min={1} max={365}
                            onChange={e => setRecInterval(+e.target.value)} />
                          <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>days</span>
                        </div>
                      )}

                      {recType === 'weekdays' && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {['mon','tue','wed','thu','fri','sat','sun'].map(d => {
                            const checked = recWeekdays.includes(d);
                            const toggle  = () => setRecWeekdays(prev =>
                              checked ? prev.filter(x => x !== d) : [...prev, d]
                            );
                            return (
                              <label key={d} onClick={toggle}
                                style={{
                                  padding: '6px 12px', borderRadius: 20, cursor: 'pointer',
                                  fontSize: 13, fontWeight: 500, userSelect: 'none',
                                  background: checked ? 'var(--primary)' : 'var(--bg-secondary)',
                                  color: checked ? '#fff' : 'var(--text-muted)',
                                  border: `1px solid ${checked ? 'var(--primary)' : 'var(--border)'}`,
                                }}>
                                {{mon:'Mon',tue:'Tue',wed:'Wed',thu:'Thu',fri:'Fri',sat:'Sat',sun:'Sun'}[d]}
                              </label>
                            );
                          })}
                        </div>
                      )}

                      {recType === 'weekdays' && recWeekdays.length === 0 && (
                        <p className="bc-hint" style={{ color: '#dc2626' }}>Pick at least one day.</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Summary */}
              <div className="bc-summary">
                <div className="bc-summary-title">Campaign summary</div>
                <div className="bc-summary-row">
                  <span>Audience</span>
                  <span>
                    {{ all: 'All users', active_days: `Active ${activeDays}d`,
                       storage_key: `${storageKey}=${storageVal}`,
                       last_command: `Stopped at ${lastCmd || '?'}` }[audienceType]}
                  </span>
                </div>
                <div className="bc-summary-row">
                  <span>Message</span>
                  <span>{{ text: 'Text', buttons: 'Buttons', cta: 'CTA Button', image: 'Image', template: 'Template' }[msgType]}</span>
                </div>
                <div className="bc-summary-row">
                  <span>Send</span>
                  <span>
                    {when === 'now' && 'Immediately (queued)'}
                    {when === 'scheduled' && `Scheduled ${schedAt}`}
                    {when === 'inactivity' && `${inactivityHours || 0}h after stopping at ${lastCmd || '?'}`}
                  </span>
                </div>
                {recEnabled && when !== 'inactivity' && (
                  <div className="bc-summary-row">
                    <span>Repeats</span>
                    <span>{recurrenceLabel(buildRecurrence()) ?? '—'}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {step > 0 && (
            <button className="btn btn-outline" onClick={() => setStep(s => s - 1)}>
              ← Back
            </button>
          )}
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          {step < STEPS.length - 1 ? (
            <button className="btn btn-primary" disabled={!canNext()}
              onClick={() => setStep(s => s + 1)}>
              Next →
            </button>
          ) : (
            <button className="btn btn-primary" disabled={!canNext() || saving}
              onClick={handleSave}>
              {saving
                ? (isEdit ? 'Saving…' : 'Creating…')
                : isEdit
                  ? '💾 Save changes'
                  : when === 'now'
                    ? '▶ Create & Queue'
                    : when === 'inactivity'
                      ? '⏱ Create & Activate'
                      : '📅 Create & Schedule'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Campaign Detail Modal ──────────────────────────────────────
function CampaignDetailModal({ campaign, bId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBroadcast(bId, campaign.bId)
      .then(r => setDetail(r.data))
      .catch(() => toast.error('Failed to load campaign detail'))
      .finally(() => setLoading(false));
  }, [bId, campaign.bId]);

  const c = campaign;
  const recipients = detail?.recipients ?? [];

  const deliveredCount = recipients.filter(r => r.rDeliveredAt).length;
  const readCount      = recipients.filter(r => r.rReadAt).length;
  const sentPct        = pct(c.bSentCount,   c.bTotalCount);
  const delPct         = pct(deliveredCount,  c.bTotalCount);
  const readPct        = pct(readCount,       c.bTotalCount);
  const failPct        = pct(c.bFailedCount,  c.bTotalCount);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal bc-detail-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">📊 {c.bName}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto', maxHeight: '70vh' }}>
          {/* Stats */}
          {c.bTotalCount > 0 && (
            <div className="bc-detail-stats">
              {[
                { label: 'Sent',      val: c.bSentCount,    pct: sentPct, color: '#16a34a' },
                { label: 'Delivered', val: deliveredCount,  pct: delPct,  color: '#2563eb' },
                { label: 'Read',      val: readCount,       pct: readPct, color: '#7c3aed' },
                { label: 'Failed',    val: c.bFailedCount,  pct: failPct, color: '#dc2626' },
              ].map(s => (
                <div key={s.label} className="bc-detail-stat">
                  <div className="bc-detail-stat-num" style={{ color: s.color }}>
                    {s.val.toLocaleString()}
                  </div>
                  <div className="bc-detail-stat-label">{s.label}</div>
                  <ProgressBar value={s.pct} color={s.color} />
                  <div className="bc-detail-stat-pct">{s.pct}%</div>
                </div>
              ))}
            </div>
          )}

          <div className="bc-detail-info">
            <span><b>Status:</b> <StatusBadge status={c.bStatus} /></span>
            {c.bStartedAt  && <span><b>Started:</b> {fmt(c.bStartedAt)}</span>}
            {c.bFinishedAt && <span><b>Finished:</b> {fmt(c.bFinishedAt)}</span>}
            {c.bScheduledAt && <span><b>Scheduled:</b> {fmt(c.bScheduledAt)}</span>}
            {c.bSendType === 'inactivity' && (
              <span><b>Trigger:</b> {c.bInactivityHours}h of inactivity</span>
            )}
            {recurrenceLabel(c.bRecurrence) && (
              <span><b>Repeats:</b> {recurrenceLabel(c.bRecurrence)}</span>
            )}
            {c.bNextRunAt && c.bStatus === 'recurring' && (
              <span><b>Next run:</b> {fmt(c.bNextRunAt)}</span>
            )}
            <span><b>Total audience:</b> {c.bTotalCount.toLocaleString()}</span>
          </div>

          {/* Recipient table */}
          {loading ? (
            <div className="tab-loading"><span className="spinner spinner-dark" /> Loading recipients…</div>
          ) : recipients.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: '20px 0' }}>
              {c.bSendType === 'inactivity'
                ? 'No one has crossed the inactivity threshold yet — this campaign keeps watching automatically.'
                : 'No recipients yet — click Send to queue this campaign.'}
            </p>
          ) : (
            <div className="bc-recipient-table">
              <div className="bc-recipient-header">
                <span>User</span>
                <span>Status</span>
                <span>Delivered</span>
                <span>Read</span>
              </div>
              {recipients.map(r => (
                <div key={r.rId} className="bc-recipient-row">
                  <span className="bc-recipient-name">
                    {r.uName || r.rChatId}
                    {r.uPhone && <small> · {r.uPhone}</small>}
                  </span>
                  <span>
                    {r.rStatus === 'sent'      && <span className="bc-pill green"  >Sent</span>}
                    {r.rStatus === 'delivered' && <span className="bc-pill blue"   >Delivered</span>}
                    {r.rStatus === 'read'      && <span className="bc-pill purple" >Read</span>}
                    {r.rStatus === 'failed'    && <span className="bc-pill red" title={r.rError}>Failed</span>}
                    {r.rStatus === 'pending'   && <span className="bc-pill grey"   >Pending</span>}
                  </span>
                  <span className="bc-recipient-time">{r.rDeliveredAt ? fmt(r.rDeliveredAt) : '—'}</span>
                  <span className="bc-recipient-time">{r.rReadAt      ? fmt(r.rReadAt)      : '—'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Confirm modal (generic) ────────────────────────────────────
function ConfirmModal({ campaign, title, message, confirmLabel, confirmStyle, onConfirm, onClose }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <div className="modal-title">{title ?? '⚠️ Delete Campaign'}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p>{message ?? <>Delete <b>"{campaign.bName}"</b>? This cannot be undone.</>}</p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn" style={confirmStyle ?? { background: '#dc2626', color: '#fff' }}
            onClick={onConfirm}>{confirmLabel ?? 'Delete'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Test-send a campaign's message to a single number ──────────
function TestSendModal({ campaign, onConfirm, onClose }) {
  const [number, setNumber] = useState('');
  const [sending, setSending] = useState(false);

  async function handleSend() {
    if (!number.trim()) return;
    setSending(true);
    await onConfirm(number.trim());
    setSending(false);
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <div className="modal-title">🧪 Test Send</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ marginBottom: 10 }}>
            Send <b>"{campaign.bName}"</b>'s message to a single number, without affecting
            this campaign's real recipients or stats.
          </p>
          <input
            type="text"
            className="input"
            placeholder="e.g. 2348167564907 (country code, no +)"
            value={number}
            onChange={e => setNumber(e.target.value)}
            autoFocus
            onKeyDown={e => { if (e.key === 'Enter') handleSend(); }}
          />
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSend} disabled={sending || !number.trim()}>
            {sending ? 'Sending…' : 'Send Test'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main tab ───────────────────────────────────────────────────
export default function BroadcastsTab({ bId }) {
  const [campaigns, setCampaigns] = useState([]);
  const [commands,  setCommands]  = useState([]);
  const [loading,   setLoading]   = useState(true);

  const [showNew,    setShowNew]    = useState(false);
  const [editCamp,   setEditCamp]   = useState(null);
  const [viewCamp,   setViewCamp]   = useState(null);
  const [deleteCamp, setDeleteCamp] = useState(null);
  const [stopCamp,   setStopCamp]   = useState(null); // campaign pending stop confirm
  const [testCamp,   setTestCamp]   = useState(null); // campaign pending test-send

  const load = useCallback(() => {
    setLoading(true);
    listBroadcasts(bId)
      .then(r => {
        setCampaigns(r.data.campaigns || []);
        setCommands(r.data.commands   || []);
      })
      .catch(e => {
        console.error('listBroadcasts error:', e?.response?.status, e?.response?.data);
        toast.error(e?.response?.data?.message || 'Failed to load campaigns');
      })
      .finally(() => setLoading(false));
  }, [bId]);

  useEffect(() => { load(); }, [load]);

  const LARGE_SEND_THRESHOLD = 100;

  async function confirmLargeSendIfNeeded(c) {
    try {
      const r = await previewBroadcastAudience(bId, c.bId);
      const total = r.data?.total ?? 0;
      if (total > LARGE_SEND_THRESHOLD) {
        return window.confirm(
          `This will message ${total.toLocaleString()} users. Continue?`
        );
      }
      return true;
    } catch {
      // If the preview call fails for any reason, don't block sending —
      // just skip the confirmation rather than breaking the flow.
      return true;
    }
  }

  async function handleSend(c) {
    if (!(await confirmLargeSendIfNeeded(c))) return;
    const tid = toast.loading('Queuing campaign…');
    try {
      const r = await sendBroadcast(bId, c.bId);
      toast.success(`Queued for ${r.data.total} users`, { id: tid });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to queue', { id: tid });
    }
  }

  async function handleResend(c) {
    if (!(await confirmLargeSendIfNeeded(c))) return;
    const tid = toast.loading('Re-queuing campaign…');
    try {
      const r = await resendBroadcast(bId, c.bId);
      toast.success(`Re-queued for ${r.data.total} users`, { id: tid });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to resend', { id: tid });
    }
  }

  async function handlePause(c) {
    const tid = toast.loading('Pausing campaign…');
    try {
      await pauseBroadcast(bId, c.bId);
      toast.success('Campaign paused', { id: tid });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to pause campaign', { id: tid });
    }
  }

  async function handleResume(c) {
    const tid = toast.loading('Resuming campaign…');
    try {
      await resumeBroadcast(bId, c.bId);
      toast.success('Campaign resumed', { id: tid });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to resume campaign', { id: tid });
    }
  }

  async function handleTestSend(testNumber) {
    if (!testCamp) return;
    const tid = toast.loading('Sending test message…');
    try {
      await testSendBroadcast(bId, testCamp.bId, testNumber);
      toast.success('Test message sent', { id: tid });
      setTestCamp(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to send test message', { id: tid });
    }
  }

  async function handleStop() {
    if (!stopCamp) return;
    const tid = toast.loading('Stopping campaign…');
    try {
      await stopBroadcast(bId, stopCamp.bId);
      toast.success('Campaign stopped', { id: tid });
      setStopCamp(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to stop campaign', { id: tid });
    }
  }

  async function handleDelete() {
    if (!deleteCamp) return;
    try {
      await deleteBroadcast(bId, deleteCamp.bId);
      toast.success('Campaign deleted');
      setDeleteCamp(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to delete');
    }
  }

  return (
    <div className="tab-content">
      {/* Header */}
      <div className="bc-header">
        <div>
          <h2 className="bc-title">📢 Broadcasts</h2>
          <p className="bc-subtitle">
            Send targeted messages to segments of your bot users.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>
          + New Campaign
        </button>
      </div>

      {/* Body */}
      {loading ? (
        <div className="tab-loading">
          <span className="spinner spinner-dark" /> Loading campaigns…
        </div>
      ) : campaigns.length === 0 ? (
        <div className="tab-empty">
          <div className="tab-empty-icon">📢</div>
          <div className="tab-empty-title">No campaigns yet</div>
          <div className="tab-empty-desc">
            Create your first broadcast campaign to reach your users.
          </div>
          <button className="btn btn-primary" style={{ marginTop: 16 }}
            onClick={() => setShowNew(true)}>
            + New Campaign
          </button>
        </div>
      ) : (
        <div className="bc-list">
          {campaigns.map(c => (
            <CampaignRow key={c.bId} c={c}
              onView={setViewCamp}
              onSend={handleSend}
              onResend={handleResend}
              onPause={handlePause}
              onResume={handleResume}
              onTestSend={setTestCamp}
              onStop={setStopCamp}
              onEdit={setEditCamp}
              onDelete={setDeleteCamp} />
          ))}
        </div>
      )}

      {/* Modals */}
      {showNew && (
        <NewBroadcastModal bId={bId} commands={commands}
          onSaved={() => { setShowNew(false); load(); }}
          onClose={() => setShowNew(false)} />
      )}

      {editCamp && (
        <NewBroadcastModal bId={bId} commands={commands} campaign={editCamp}
          onSaved={() => { setEditCamp(null); load(); }}
          onClose={() => setEditCamp(null)} />
      )}

      {viewCamp && (
        <CampaignDetailModal campaign={viewCamp} bId={bId}
          onClose={() => setViewCamp(null)} />
      )}

      {stopCamp && (
        <ConfirmModal
          campaign={stopCamp}
          title="⏹ Stop Campaign"
          message={<>Stop <b>"{stopCamp.bName}"</b>? Any messages already sending will finish, but the remaining queue will be cancelled. You can resend it later.</>}
          confirmLabel="Stop Campaign"
          confirmStyle={{ background: '#dc2626', color: '#fff' }}
          onConfirm={handleStop}
          onClose={() => setStopCamp(null)} />
      )}

      {deleteCamp && (
        <ConfirmModal campaign={deleteCamp}
          onConfirm={handleDelete}
          onClose={() => setDeleteCamp(null)} />
      )}

      {testCamp && (
        <TestSendModal
          campaign={testCamp}
          onConfirm={handleTestSend}
          onClose={() => setTestCamp(null)} />
      )}
    </div>
  );
}